import { MutableRefObject, useCallback } from 'react'
import { LocalDesktopManifestInfo } from '../RoomContext'
import { UserContextState } from '../UserContext'
import { stripSemverVersion } from '../utils'
import { env } from './EnvironmentVariables'
import { MusePeer, PeerManifest } from './RoomContextMusePeer'
import useRoomContextRef from './useRoomContextRef'
import useUserContextRef from './useUserContextRef'
import { decodeExtendedDeviceId } from '../Logic'
import _ from 'lodash'
import useAppContextRef from './useAppContextRef'
import { sessionViewModeState } from '@/pages/SessionPage/internal/shared/useViewMode'

export default function useManifestMethods() {
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()
    const { appStateRef } = useAppContextRef()

    const generateMutedChgrpsLookup = useCallback(() => {
        const mutedChgrpsLookup: { [key: string]: number[][] } = {}
        Object.keys(roomStateRef.current.myAudioMuteState || {}).forEach((deviceIdExt) => {
            if (roomStateRef.current.myAudioMuteState[deviceIdExt] === true) {
                // deviceIdExt is extended deviceId, e.g. 7f756379a5ea9?chgrp=[0]
                const result = decodeExtendedDeviceId(deviceIdExt)
                if ('chgrp' in result) {
                    if (mutedChgrpsLookup[result.deviceId] == null) {
                        mutedChgrpsLookup[result.deviceId] = []
                    }
                    mutedChgrpsLookup[result.deviceId].push(result.chgrp)
                }
            }
        })

        return mutedChgrpsLookup
    }, [])

    const generateP2pManifest = (peer: MusePeer) => {
        const mutedChgrpsLookup = generateMutedChgrpsLookup()

        // make next PeerManifest.
        const m: PeerManifest = {
            localManifestV: roomStateRef.current.localManifestV,

            clientType: userStateRef.current.clientType,

            isScreenRecording: roomStateRef.current.isScreenRecording != null,
            isAudioDeprioritized: roomStateRef.current.isMyAudioDeprioritized,

            localP2pAudios: peer.localP2pAudios.map((a) => {
                const deviceId = a.track?.info?.deviceId || ''
                return {
                    stream: { id: a.stream.id },
                    track: { id: a.track.id },
                    info: a.track.info,
                    controlStates: {
                        isMuted: roomStateRef.current.myAudioMuteState[deviceId] === true,
                        mutedChannelGroups: mutedChgrpsLookup[deviceId] || [[]],
                    },
                }
            }),

            localP2pVideo: peer.localP2pVideo
                ? {
                      stream: { id: peer.localP2pVideo.stream.id },
                      controlStates: { isMuted: roomStateRef.current.myVideoMuteState === true },
                  }
                : null,

            localP2pDesktop:
                roomStateRef.current.expectedNetworkTopology === 'p2p' ? mkLocalDesktopManifest(userStateRef) : null,

            localP2pMidi: peer.localP2pMidiDevices.map((a) => ({
                sourceId: a.sourceId,
                label: a.label,
                isMuted: !!roomStateRef.current.myMidiMuteState[a.sourceId],
            })),

            localSfuAudios: [],
            localSfuVideo: null,
            localSfuDesktop: null,
            localSfuMidi: [],

            screenControlRequest: roomStateRef.current.screenControlRequest,
            lastScreenControlRequestResponse: roomStateRef.current.lastScreenControlRequestResponse,

            galleryClientOrdering: roomStateRef.current.isHost ? [...sessionViewModeState.mode.videoOrder] : null,
            videoHiddenSessionIds: roomStateRef.current.isHost ? roomStateRef.current.videoHiddenSessionIds : [],
            applyHostGalleryPrefsToAllParticipants: roomStateRef.current.isHost
                ? appStateRef.current.appSettings.applyHostGalleryPrefsToAllParticipants
                : false,
        }
        return m
    }

    const generateBaseSfuManifest = useCallback(() => {
        const mutedChgrpsLookup = generateMutedChgrpsLookup()

        // make next PeerManifest.
        const m = {
            localManifestV: roomStateRef.current.localManifestV,

            clientType: userStateRef.current.clientType,

            isScreenRecording: roomStateRef.current.isScreenRecording != null,
            isAudioDeprioritized: roomStateRef.current.isMyAudioDeprioritized,

            localP2pAudios: [],
            localP2pVideo: null,
            localP2pDesktop: null,
            localP2pMidi: [],

            localSfuAudios: roomStateRef.current.publishedSfuAudios.map((a) => {
                const deviceId = a.info?.deviceId || ''
                return {
                    info: a.info,
                    controlStates: {
                        isMuted: roomStateRef.current.myAudioMuteState[deviceId] === true,
                        mutedChannelGroups: mutedChgrpsLookup[deviceId] || [[]],
                    },
                }
            }),

            localSfuVideo: userStateRef.current.mediaStreams.video
                ? {
                      controlStates: { isMuted: roomStateRef.current.myVideoMuteState === true },
                  }
                : null,

            localSfuDesktop:
                roomStateRef.current.expectedNetworkTopology === 'sfu' ? mkLocalDesktopManifest(userStateRef) : null,

            localSfuMidi: roomStateRef.current.publishedSfuMidi.map((a) => ({
                sourceId: a.sourceId,
                label: a.label,
                isMuted: !!roomStateRef.current.myMidiMuteState[a.sourceId],
            })),

            galleryClientOrdering: roomStateRef.current.isHost ? [...sessionViewModeState.mode.videoOrder] : null,
            videoHiddenSessionIds: roomStateRef.current.isHost ? roomStateRef.current.videoHiddenSessionIds : [],
            applyHostGalleryPrefsToAllParticipants: roomStateRef.current.isHost
                ? appStateRef.current.appSettings.applyHostGalleryPrefsToAllParticipants
                : false,
        }
        return m
    }, [roomStateRef, userStateRef])

    const generateSfuManifest = useCallback(() => {
        const m1 = generateBaseSfuManifest()
        const m2 = {
            screenControlRequest: roomStateRef.current.screenControlRequest,
            lastScreenControlRequestResponse: roomStateRef.current.lastScreenControlRequestResponse,
        }
        return { ...m1, ...m2 } as PeerManifest
    }, [])

    const generateLegacySfuManifest = useCallback((peer: MusePeer) => {
        const m = generateSfuManifest()
        if (m.screenControlRequest.receiverSessionId !== peer.sessionId) {
            m.screenControlRequest = { reqId: 0, receiverSessionId: '', op: '' }
        }

        if (peer.sessionId !== roomStateRef.current.lastScreenControlRequestResponse.requesterSessionId) {
            const match = _.findLast(
                roomStateRef.current.screenControlRequestResponseHistory,
                (a) => a.requesterSessionId === peer.sessionId && a.status === 'allowed'
            )
            if (match) {
                m.lastScreenControlRequestResponse = { reqId: match.reqId, status: 'revoked' }
            }
        }

        return m
    }, [])

    return {
        generateP2pManifest,
        generateSfuManifest,
        generateLegacySfuManifest,
    }
}

function mkLocalDesktopManifest(userStateRef: MutableRefObject<UserContextState>) {
    const stream = userStateRef.current.mediaStreams.desktop
    const sourceInfo = userStateRef.current.mediaSelection.desktopSourceInfo
    if (stream instanceof MediaStream) {
        return {
            stream: { id: stream.id },
            info: {
                appVersion: stripSemverVersion(env.APP_VERSION) || null,
                screenIndex: sourceInfo?.screenIndex != null ? sourceInfo?.screenIndex : null,
                scaleFactor: sourceInfo?.scaleFactor || null,
                screenWidth: sourceInfo?.screenWidth || null,
                screenHeight: sourceInfo?.screenHeight || null,
                clientKind: env.IS_ELECTRON ? 'electron-app' : 'browser',
            } as LocalDesktopManifestInfo,
        }
    } else {
        return null
    }
}
